import { computed } from 'vue';

/**
 * v3 chrome navigation model (new UI, ui_layout='V2') — the legacy sibling of
 * rm-portal-beta's `navItems`. Single source of truth for the three v3 chrome
 * areas: the app RAIL, the per-page ToC (sub-nav, with optional labelled
 * sections), and the active-area lookup the header/rail use for highlighting.
 *
 * Reads the nav declaration each route carries inline on `meta.v3` (see
 * router/index.js) plus `router.v3Groups` (synthetic containers with no page of
 * their own), and applies the SAME permission gate the router guard / qaActions
 * use — so nav never disagrees with permissions.
 *
 *   meta.v3.rail / .parent / .section / .order / .icon
 *   group landing = its lowest-order permitted child
 *
 * The old menu is untouched — it still derives from `newUi`/`oldUi`/`section`.
 *
 * @param {Object} opts
 * @param {any}    opts.router       the vue-router instance ($router)
 * @param {any}    opts.loginState   the $loginState plugin (user perms)
 * @param {() => (string|undefined)} opts.getCurrentRouteName  reactive current route name
 */
// The route permission gate — mirrors the qaActions gate in App.vue / the
// router guard so nav (and anything else that links to a page) never
// disagrees with permissions. Standalone so widgets can reuse it.
export function isPermittedRoute(r, loginState) {
  const m = r && r.meta;
  if (!m) return false;
  if (m.allowAll) return true;
  if (!m.newUi) return false;
  const user = (loginState && loginState.user) || {};
  const permitted = (user.userPermittedRoutes || []).some((pr) => pr === r.name);
  const landing = (user.landingPageConfigs || []).some((lp) => lp.page_route === r.name);
  return permitted || landing;
}

/**
 * Can this component navigate to a widget `destination`? The old landing
 * config only ever surfaced links the user could actually reach — v3 widgets
 * (QuickLinks / LinkList / ActionPanel / FeatureTiles / StatRow / Hero /
 * chart footer links) call this before rendering a click-through.
 *   · 'create'      → gated on the user having create options (header parity)
 *   · http(s) URL   → external, always allowed
 *   · anything else → a route name; must exist AND pass isPermittedRoute
 * Takes the component instance (`this`) so Vue 2 options components can call
 * it in one line: `canNavigateTo(this, dest)`.
 */
export function canNavigateTo(vm, dest) {
  if (!dest) return false;
  if (dest === 'create') {
    const user = (vm.$loginState && vm.$loginState.user) || {};
    return (user.createDocumentDialogOptions || []).length > 0;
  }
  if (/^https?:\/\//i.test(dest)) return true;
  const routes = vm.$router && typeof vm.$router.getRoutes === 'function' ? vm.$router.getRoutes() : [];
  const r = routes.find((x) => x.name === dest);
  return !!r && isPermittedRoute(r, vm.$loginState);
}

export function useV3Nav({ router, loginState, getCurrentRouteName = () => undefined } = {}) {
  // Mirror the qaActions gate in App.vue so the rail/ToC show exactly the routes
  // the user may reach.
  function isPermitted(r) {
    return isPermittedRoute(r, loginState);
  }

  const allRoutes = () => (router && typeof router.getRoutes === 'function' ? router.getRoutes() : []);
  const permittedRoutes = () => allRoutes().filter(isPermitted);
  const groups = () => (router && Array.isArray(router.v3Groups) ? router.v3Groups : []);

  const v3 = (r) => (r && r.meta && r.meta.v3) || null;

  // The v3 rail renders icons with Material Symbols (the loaded font); take the
  // first token and prefer the v3 override when the old menu's icon is mdi-based.
  const iconName = (raw) => String(raw || '').trim().split(/\s+/)[0] || '';
  const routeIcon = (r) => iconName((v3(r) && v3(r).icon) || (r.meta && r.meta.icon));

  function toItem(r) {
    const cfg = v3(r) || {};
    return {
      key: r.name,
      routeName: r.name,
      to: { name: r.name },
      text: (r.meta && r.meta.text) || r.name,
      icon: routeIcon(r),
      order: cfg.order != null ? cfg.order : 100,
      section: cfg.section || null,
      isGroup: false,
    };
  }

  // Permitted children of an area key (group key or rail route name), ordered.
  // A child may set `v3.tocText` to show a different label in the ToC than its
  // rail/route text (e.g. rail "Levelling" → ToC "Levelling Control Centre").
  function childrenOf(areaKey) {
    if (!areaKey) return [];
    return permittedRoutes()
      .filter((r) => v3(r) && v3(r).parent === areaKey)
      .map((r) => {
        const item = toItem(r);
        const tocText = (v3(r) || {}).tocText;
        return tocText ? { ...item, text: tocText } : item;
      })
      .sort((a, b) => a.order - b.order || String(a.text).localeCompare(String(b.text)));
  }

  // "Home" resolves to the user's default landing page — a DYNAMIC landing route
  // named after its `page_route` (e.g. 'homepage'), set via the `default_page`
  // setting — falling back to the static `home` route.
  const isGroupSection = (sec) =>
    !!(sec && groups().some((g) => String(g.key).toLowerCase() === String(sec).toLowerCase() || g.text === sec));
  const groupKeyForSection = (sec) => {
    const g = groups().find((x) => String(x.key).toLowerCase() === String(sec).toLowerCase() || x.text === sec);
    return g ? g.key : null;
  };
  const isHomeLanding = (r) => !!(r && r.meta && r.meta.landingPageId && !isGroupSection(r.meta.section));

  function homeRouteName() {
    let dp = null;
    try {
      dp = loginState && typeof loginState.getUserSetting === 'function' ? loginState.getUserSetting('default_page') : null;
    } catch {
      dp = null;
    }
    if (dp && allRoutes().some((r) => r.name === dp)) return dp;
    const landing = allRoutes().find(isHomeLanding);
    if (landing) return landing.name;
    return 'home';
  }

  // Rail = routes with meta.v3.rail + groups (hidden when no permitted child),
  // ordered by rail position. Home is pinned first and keyed 'home' so it
  // targets the user's default landing page and highlights on any Home landing.
  const railItems = computed(() => {
    const real = permittedRoutes()
      .filter((r) => v3(r) && v3(r).rail != null && r.name !== 'home')
      .map((r) => ({ ...toItem(r), order: v3(r).rail }));

    const groupItems = groups()
      .map((g) => {
        const children = childrenOf(g.key);
        const landing = children[0] || null; // lowest-order permitted child
        return {
          key: g.key,
          routeName: landing ? landing.routeName : undefined,
          to: landing ? landing.to : undefined,
          text: g.text || g.key,
          icon: iconName(g.icon),
          order: g.rail != null ? g.rail : 100,
          isGroup: true,
          children,
        };
      })
      .filter((g) => g.children.length > 0);

    const home = {
      key: 'home',
      routeName: homeRouteName(),
      to: { name: homeRouteName() },
      text: 'Home',
      icon: 'home',
      order: 0,
      isGroup: false,
    };

    return [home, ...real, ...groupItems].sort((a, b) => a.order - b.order);
  });

  // ToC for an area: its permitted children (each carries `section` for the
  // drawer's labelled sub-group headers).
  function tocFor(areaKey) {
    return childrenOf(areaKey);
  }

  // Which top-level area the current route belongs to: home (incl. dynamic Home
  // landing pages), itself if it's a rail route, else its v3 parent; Admin/
  // Reporting landing pages map to their group.
  const activeArea = computed(() => {
    const name = getCurrentRouteName();
    if (!name) return null;
    if (name === 'home' || name === homeRouteName()) return 'home';
    const r = allRoutes().find((x) => x.name === name);
    if (!r || !r.meta) return null;
    const cfg = v3(r);
    if (cfg && cfg.rail != null) return r.name;
    if (cfg && cfg.parent) return cfg.parent;
    if (r.meta.landingPageId) {
      return groupKeyForSection(r.meta.section) || 'home';
    }
    return null;
  });

  const activeToc = computed(() => tocFor(activeArea.value));

  return { railItems, tocFor, activeArea, activeToc, isPermitted };
}
